import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as XLSX from 'xlsx';
import { SignoCentroMedicoService } from 'src/app/services/entidades/signo-centros-medicos.service';
import { ProvinciaService } from '@services/provincias/provincias.service';
import { CatalogoService } from 'src/app/services/shared/catalogo.service';
import { SignoLocalidadService } from 'src/app/services/entidades/signo-localidades.service';
import { SignoGerenciasService } from 'src/app/components/entidades/gerencias/gerencias-service/gerencias.service';
import { EntidadesKeys } from '@enum/entidades-keys.enum';
import { CentrosDetalleModalComponent } from './centros-detalle-modal/centros-detalle-modal.component';
import { LocalidadesZonaModalComponent } from './localidades-zona-modal/localidades-zona-modal.component';

@Component({
  selector: 'app-centros-estadisticas',
  templateUrl: './centros-estadisticas.component.html',
  styleUrls: ['./centros-estadisticas.component.scss']
})
export class CentrosEstadisticasComponent implements OnInit, OnDestroy {

  isLoading = true;
  isLoadingGrid = false;
  grid = { centros: [] as any[], total: 0 };
  provincias: any[] = [];
  localidades: any[] = [];
  rubros: any[] = [];
  especialidades: any[] = [];
  gerenciadoras: any[] = [];
  zonas: any[] = [];

  filtros = { provinciaId: '', localidadId: '', rubroId: '', especialidadId: '', gerenciadoraId: '' };
  stats: any = null;
  busqueda = '';

  page = 0;
  pageSize = 10;
  private busquedaTimeout: any;

  constructor(
    private centroService: SignoCentroMedicoService,
    private provinciaService: ProvinciaService,
    private catalogoService: CatalogoService,
    private localidadService: SignoLocalidadService,
    private gerenciasService: SignoGerenciasService,
    private dialog: MatDialog,
  ) {}

  async ngOnInit() {
    await this.cargarCatalogos();
    await this.cargarEstadisticas();
    await this.cargarCentros();
  }

  ngOnDestroy() {
    if (this.busquedaTimeout) { clearTimeout(this.busquedaTimeout); }
  }

  async cargarCatalogos() {
    try {
      this.provincias = await this.provinciaService.findAll();
      this.provincias.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));

      this.localidades = await this.localidadService.getLocalidades();
      this.localidades.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));

      this.rubros = await this.catalogoService.findDescendantsByKey(EntidadesKeys.RUBROS);
      this.rubros.sort((a, b) => (a.concepto || '').localeCompare(b.concepto || '', 'es'));

      this.especialidades = await this.catalogoService.findDescendantsByKey(EntidadesKeys.ESPECIALIDADES);
      this.especialidades.sort((a, b) => (a.concepto || '').localeCompare(b.concepto || '', 'es'));

      this.gerenciadoras = await this.gerenciasService.getGerencias();
      this.gerenciadoras.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));

      this.zonas = await this.catalogoService.findDescendantsByKey(EntidadesKeys.ZONAS);
      this.zonas.sort((a, b) => (a.concepto || '').localeCompare(b.concepto || '', 'es'));
    } catch (e) {
      console.error('Error cargando catálogos para estadísticas:', e);
    }
  }

  async cargarEstadisticas() {
    this.isLoading = true;
    try {
      this.stats = await this.centroService.getEstadisticas(this.filtrosPayload());
    } catch (e) {
      console.error('Error cargando estadísticas:', e);
    } finally {
      this.isLoading = false;
    }
  }

  async cargarCentros() {
    this.isLoadingGrid = true;
    try {
      this.grid = await this.centroService.getCentros(this.filtrosPayload());
    } catch (e) {
      console.error('Error cargando centros:', e);
    } finally {
      this.isLoadingGrid = false;
    }
  }

  cargarTodo() {
    this.cargarEstadisticas();
    this.cargarCentros();
  }

  filtrosPayload() {
    const f: any = {};
    if (this.filtros.provinciaId) { f.provinciaId = this.filtros.provinciaId; }
    if (this.filtros.localidadId) { f.localidadId = this.filtros.localidadId; }
    if (this.filtros.rubroId) { f.rubroId = this.filtros.rubroId; }
    if (this.filtros.especialidadId) { f.especialidadId = this.filtros.especialidadId; }
    if (this.filtros.gerenciadoraId) { f.gerenciadoraId = this.filtros.gerenciadoraId; }
    if (this.busqueda) { f.busqueda = this.busqueda; }
    f.page = this.page;
    f.pageSize = this.pageSize;
    return f;
  }

  onProvinciaChange() {
    this.filtros.localidadId = '';
    this.page = 0;
    this.cargarTodo();
  }

  aplicar() { this.page = 0; this.cargarTodo(); }

  limpiar() {
    this.filtros = { provinciaId: '', localidadId: '', rubroId: '', especialidadId: '', gerenciadoraId: '' };
    this.busqueda = '';
    this.page = 0;
    this.cargarTodo();
  }

  verCentros(titulo: string, filtros: any) {
    this.dialog.open(CentrosDetalleModalComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: { titulo, filtros },
    });
  }

  verCentrosProvincia(nombre: string) {
    if (nombre === '(Sin provincia)') {
      this.verCentros('Centros sin provincia', { sinProvincia: true });
      return;
    }
    const p = this.provincias.find(x => x.nombre === nombre);
    if (p) { this.verCentros('Centros de la provincia ' + nombre, { provinciaId: p._id }); }
  }

  verCentrosLocalidad(nombre: string) {
    const l = this.localidades.find(x => x.nombre === nombre);
    if (l) { this.verCentros('Centros de la localidad ' + nombre, { localidadId: l._id }); }
  }

  verCentrosGerenciadora(row: any) {
    if (row && row.gerenciadoraId) {
      this.verCentros('Centros de la gerenciadora ' + row.gerenciadora, { gerenciadoraId: row.gerenciadoraId });
    }
  }

  verCentrosZona(row: any) {
    if (row && row.zonaId) {
      this.verCentros('Centros de la zona ' + row.zona, { zonaId: row.zonaId });
    }
  }

  verCentrosDeshabilitados() {
    this.verCentros('Centros deshabilitados', { activo: false });
  }

  verLocalidadesZona(row: any) {
    if (!row || !row.zonaId) { return; }
    const localidades = this.localidades
      .filter(l => {
        const z = l.zona;
        const zid = typeof z === 'string' ? z : (z && z._id) ? z._id : null;
        return zid === row.zonaId;
      })
      .map(l => ({
        nombre: l.nombre,
        provincia: (l.provincia && (l.provincia.nombre || (this.provinciaLabel(l.provincia._id)))) || '',
      }))
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
    this.dialog.open(LocalidadesZonaModalComponent, {
      width: '700px',
      maxWidth: '95vw',
      data: { titulo: 'Localidades de la zona ' + row.zona, localidades },
    });
  }

  provinciaLabel(id: string) { const p = this.provincias.find(x => x._id === id); return p ? p.nombre : ''; }
  localidadLabel(id: string) { const l = this.localidades.find(x => x._id === id); return l ? l.nombre : ''; }
  rubroLabel(id: string) { const r = this.rubros.find(x => x._id === id); return r ? r.concepto : ''; }
  especialidadLabel(id: string) { const e = this.especialidades.find(x => x._id === id); return e ? e.concepto : ''; }
  gerenciadoraLabel(id: string) { const g = this.gerenciadoras.find(x => x._id === id); return g ? g.nombre : ''; }

  get chipsActivos() {
    const chips: Array<{ label: string }> = [];
    if (this.filtros.provinciaId) { chips.push({ label: 'Provincia: ' + this.provinciaLabel(this.filtros.provinciaId) }); }
    if (this.filtros.localidadId) { chips.push({ label: 'Localidad: ' + this.localidadLabel(this.filtros.localidadId) }); }
    if (this.filtros.rubroId) { chips.push({ label: 'Rubro: ' + this.rubroLabel(this.filtros.rubroId) }); }
    if (this.filtros.especialidadId) { chips.push({ label: 'Especialidad: ' + this.especialidadLabel(this.filtros.especialidadId) }); }
    if (this.filtros.gerenciadoraId) { chips.push({ label: 'Gerenciadora: ' + this.gerenciadoraLabel(this.filtros.gerenciadoraId) }); }
    return chips;
  }

  maxPorProvincia() {
    if (!this.stats || !this.stats.porProvincia || !this.stats.porProvincia.length) { return 1; }
    return Math.max(...this.stats.porProvincia.map((p: any) => p.totalCentros), 1);
  }

  anchoBarra(v: number) { return Math.round((v / this.maxPorProvincia()) * 100); }

  async exportarExcel() {
    if (!this.stats) { return; }
    const wb = XLSX.utils.book_new();

    const resumen = [{ Centros: this.stats.totalCentros, Especialidades: this.stats.totalEspecialidades, Rubros: this.stats.totalRubros }];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumen), 'Resumen');

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((this.stats.porProvincia || []).map((p: any) => ({ Provincia: p.provincia, Centros: p.totalCentros }))), 'Provincia');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((this.stats.porLocalidad || []).map((l: any) => ({ Localidad: l.localidad, Provincia: l.provincia, Zona: l.zona, Centros: l.totalCentros }))), 'Localidad');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((this.stats.porGerenciadora || []).map((g: any) => ({ Gerenciadora: g.gerenciadora, Centros: g.totalCentros }))), 'Gerenciadora');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((this.stats.porZona || []).map((z: any) => ({ Zona: z.zona, Centros: z.totalCentros }))), 'Zona');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((this.stats.porRubro || []).map((r: any) => ({ Rubro: r.rubro, Centros: r.totalCentros }))), 'Rubro');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((this.stats.porEspecialidad || []).map((e: any) => ({ Especialidad: e.especialidad, Centros: e.totalCentros }))), 'Especialidad');

    const resp = await this.centroService.getCentros({ page: 0, pageSize: this.stats.totalCentros || 100000 });
    const centros = (resp.centros || [])
      .sort((a: any, b: any) =>
        (a.provincia || '').localeCompare(b.provincia || '', 'es') ||
        (a.localidad || '').localeCompare(b.localidad || '', 'es') ||
        (a.nombre || '').localeCompare(b.nombre || '', 'es'))
      .map((c: any) => ({
        Nombre: c.nombre,
        Localidad: c.localidad,
        Provincia: c.provincia,
        Zona: c.zona || '',
        Gerenciadora: c.gerenciadora || '',
        'Dirección': c.direccion || '',
        Especialidades: (c.especialidades || []).join(', '),
        Rubros: (c.rubros || []).join(', '),
      }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(centros), 'Centros');

    const fecha = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `estadisticas_centros_medicos_${fecha}.xlsx`);
  }

  onBusqueda() {
    if (this.busquedaTimeout) { clearTimeout(this.busquedaTimeout); }
    this.busquedaTimeout = setTimeout(() => {
      this.page = 0;
      this.cargarCentros();
    }, 350);
  }

  paginaAnterior() {
    if (this.page > 0) {
      this.page = this.page - 1;
      this.cargarCentros();
    }
  }

  paginaSiguiente() {
    if (this.page + 1 < this.totalPaginas) {
      this.page = this.page + 1;
      this.cargarCentros();
    }
  }

  get centrosPage() {
    return this.grid.centros;
  }

  get totalCentrosGrid() {
    return this.grid.total;
  }

  get totalPaginas() {
    return Math.max(1, Math.ceil(this.totalCentrosGrid / this.pageSize));
  }
}
